/**
 * `/admin/roles` — the role editor (SPEC §9.1, §10).
 *
 * "Role editor: name, then one row per granted department with All reports /
 * Specific reports and a checklist when specific."
 *
 * The grant form itself is `components/admin/GrantEditor.tsx`. Saving is a
 * full replacement — the wording on the button and beneath it says so, because
 * "I unchecked it and they can still see it" is the bug this design removes
 * and an admin should be able to predict that from the interface.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { GrantEditor } from '../../components/admin/GrantEditor'
import { Badge, Button, Card, ConfirmDialog, TextField } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import type { GrantInput, Role } from '../../lib/adminTypes'
import { ApiError } from '../../lib/api'
import { api } from '../../lib/api'

function CreateRole({ onCreated }: { onCreated: () => void }) {
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: () => adminApi.createRole({ key, name }),
    onSuccess: () => {
      setKey('')
      setName('')
      onCreated()
    },
  })
  const fields = create.error instanceof ApiError ? create.error.fields : {}

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate()
      }}
    >
      <div className="w-56">
        <TextField
          label="Key"
          value={key}
          required
          onChange={(event) => {
            setKey(event.target.value)
          }}
          error={fields.key}
        />
      </div>
      <div className="w-64">
        <TextField
          label="Name"
          value={name}
          required
          onChange={(event) => {
            setName(event.target.value)
          }}
          error={fields.name}
        />
      </div>
      <Button variant="primary" type="submit" disabled={create.isPending}>
        {create.isPending ? 'Creating…' : 'Create role'}
      </Button>
      {create.error !== null && fields.key === undefined && fields.name === undefined && (
        <div className="w-full">
          <ErrorState error={create.error} />
        </div>
      )}
    </form>
  )
}

function RoleCard({ role, onChanged }: { role: Role; onChanged: () => void }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const departments = useQuery({ queryKey: ['departments'], queryFn: api.departments })
  const reports = useQuery({ queryKey: ['admin', 'reports'], queryFn: adminApi.reports })

  const save = useMutation({
    mutationFn: (grants: readonly GrantInput[]) => adminApi.setGrants(role.id, grants),
    onSuccess: onChanged,
  })
  const remove = useMutation({
    mutationFn: () => adminApi.deleteRole(role.id),
    onSuccess: () => {
      setConfirmingDelete(false)
      onChanged()
    },
  })

  return (
    <Card
      title={role.name}
      description={role.description ?? undefined}
      actions={
        <>
          <Badge>{role.key}</Badge>
          <Badge>
            {role.user_count} {role.user_count === 1 ? 'user' : 'users'}
          </Badge>
          <Button
            variant="danger"
            onClick={() => {
              setConfirmingDelete(true)
            }}
          >
            Delete
          </Button>
        </>
      }
    >
      {departments.isPending || reports.isPending ? (
        <Skeleton rows={2} />
      ) : (
        <GrantEditor
          // Remount when the saved grants change, so the draft starts from
          // what the server actually stored rather than from a stale render.
          key={JSON.stringify(role.grants)}
          grants={role.grants}
          departments={departments.data ?? []}
          reports={reports.data ?? []}
          saving={save.isPending}
          onSave={(grants) => {
            save.mutate(grants)
          }}
        />
      )}
      {save.error !== null && (
        <div className="mt-3">
          <ErrorState error={save.error} />
        </div>
      )}
      {remove.error !== null && (
        <div className="mt-3">
          <ErrorState error={remove.error} />
        </div>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete ${role.name}?`}
        confirmLabel="Delete role"
        body={
          <>
            Its grants go with it, and {role.user_count}{' '}
            {role.user_count === 1 ? 'user loses' : 'users lose'} whatever access this role gave
            them. This cannot be undone.
          </>
        }
        onCancel={() => {
          setConfirmingDelete(false)
        }}
        onConfirm={() => {
          remove.mutate()
        }}
      />
    </Card>
  )
}

export function AdminRoles() {
  const queryClient = useQueryClient()
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: adminApi.roles })

  function onChanged(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  return (
    <div className="space-y-4">
      <Card
        title="New role"
        description="Grants are added after it exists. The key is lowercase and immutable — Entra group mappings reference it."
      >
        <CreateRole onCreated={onChanged} />
      </Card>

      {roles.isPending ? (
        <Skeleton rows={5} />
      ) : roles.error !== null ? (
        <ErrorState
          error={roles.error}
          onRetry={() => {
            void roles.refetch()
          }}
        />
      ) : (
        roles.data.map((role) => <RoleCard key={role.id} role={role} onChanged={onChanged} />)
      )}
    </div>
  )
}
